import { immerable } from "immer";
import * as THREE from "three";
import IdNameItem from "@/models/interfaces/generics/id-name-item.ts";
import FileType from "@/models/enums/file-type.ts";
import FileData3D from "./file-data-3d.ts";
import PlanOrigin from "./plan-origin.ts";
import KnownScaleContent from "./known-scale-content.ts";
import UserDefinedContent from "./user-defined-content.ts";

class BaseFileData implements IdNameItem {
    [immerable] = true;

    isDirty = false;

    id: string;

    name: string;

    fileType: FileType;

    revision: number;

    relativePath: string;

    fileMetaObject: string;

    origin: PlanOrigin;

    origins: PlanOrigin[];

    content: KnownScaleContent | UserDefinedContent | undefined; // Cela n'est valable que pour les fichiers 2D

    sitePosition: THREE.Vector3 | undefined; // Cela n'est valable que pour les fichiers 3D

    public setDirtyFlag(isDirty: boolean) {
        this.isDirty = isDirty;
    }

    public setOrigin(originId: string) {
        const found = this.origins.find(o => o.id === originId);
        if (!found) return;

        this.origin = found;

        this.origins.forEach(o => {
            o.setIsActive(o.id === found.id);
        });

        this.origin.isActive = true;
    }

    public addOrigin(origin: PlanOrigin) {
        this.origins.push(origin);
    }

    public deleteOrigin(originId: string) {
        const foundIndex = this.origins.findIndex(o => o.id === originId);
        if (foundIndex === -1) return;

        this.origins.splice(foundIndex, 1);
    }

    constructor(id: string, name: string, fileType: FileType, revision: number, relativePath: string, fileMetaObject: string, content?: KnownScaleContent | UserDefinedContent | undefined) {
        this.id = id;
        this.name = name;
        this.fileType = fileType;
        this.revision = revision;
        this.relativePath = relativePath;
        this.fileMetaObject = fileMetaObject;
        this.origin = PlanOrigin.getDefault();
        this.origins = [];
        
        if (content) {
            this.content = content;
        }
        else {
            this.content = new KnownScaleContent();
            this.content.default = true;
        }
    }

    public static createFromBaseFileData(baseFileData: BaseFileData): BaseFileData {
        const baseFile = new BaseFileData(baseFileData.id, baseFileData.name, baseFileData.fileType, baseFileData.revision, baseFileData.relativePath, baseFileData.fileMetaObject, baseFileData.content);
        baseFile.origin = baseFileData.origin;
        baseFile.origins = baseFileData.origins;

        return baseFile;
    }

    public static createFromFileData3D(fileData3D: FileData3D): BaseFileData {
        const baseFile = new BaseFileData(fileData3D.id, fileData3D.name, fileData3D.fileType, fileData3D.revision, fileData3D.relativePath, fileData3D.fileMetaObject, fileData3D.content);
        baseFile.origin = fileData3D.origin;
        baseFile.origins = fileData3D.origins;
        baseFile.sitePosition = fileData3D.sitePosition;

        return baseFile;
    }

    clone() {
        const base = new BaseFileData(this.id, this.name, this.fileType, this.revision, this.relativePath, this.fileMetaObject, this.content);
        base.origin = this.origin;
        base.origins = this.origins;
        base.sitePosition = this.sitePosition;
        return base;
    }
}

export default BaseFileData;